import { useQuery } from "@apollo/client/react";
import {
  DSButton,
  DSCardWrapper,
  DSLinkButton,
} from "@components/design-system/molecules";
import { OrgBillingInfo } from "@components/organization/BillingInfo";
import {
  faUserPlus,
  faUsers,
  faArrowLeft,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth, useOrganization, useTeam } from "@hooks";
import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router";

import { EditExistingTeamMembers } from "./EditExistingTeamMembers";
import { EditInvitedTeamMembers } from "./EditInvitedTeamMembers";
import { mapTeamData } from "./utils";
import PartyScribbles from "../../../assets/imgs/party-scribbles.png";
import GET_USER_TEAMS from "../../../graphql/queries/GetUserTeams";
import HeadingWrapper from "../../common/HeadingWrapper";
import Loading from "../../common/loading/Loading";
import { DefaultTemplatesTable } from "../../defaults/DefaultTemplatesTable";
import PageTitle from "../../layout/PageTitle";
import { DeleteTeamModal } from "../DeleteTeamModal";
import TeamModal from "../TeamModal";

export default function TeamManagement() {
  // State
  const [isShowingAddMembersModal, setIsShowingAddMembersModal] =
    useState(false);
  const [isShowingDeleteModal, setIsShowingDeleteModal] =
    useState(false);

  // Hooks
  const { teamUuid } = useParams();
  const { loadingUserData, userUuid } = useAuth();
  const { isOrgAdmin, orgUuid } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const { userTeams } = useTeam();

  // Queries
  const { data, refetch, loading } = useQuery(GET_USER_TEAMS, {
    variables: { uuid: teamUuid },
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    skip: !teamUuid,
  });

  const team = data
    ? mapTeamData(data)
    : {
        uuid: "",
        orgAllowsDirectMemberAdd: false,
        hasBillingSetup: false,
        orgIsManualBilling: false,
        users: [],
        teamInvites: [],
        orgUuid: "",
        name: "",
      };

  // Constants
  const userTeamUUIDs = userTeams?.map((team) => team.uuid);
  const isUserOnTeam = userTeamUUIDs?.includes(team?.uuid);
  const isTeamInOrganization = orgUuid === team?.orgUuid;
  const isCurrentUserAnAdmin =
    team?.users?.find((user) => user.uuid === userUuid)?.isAdmin ||
    isOrgAdmin;

  // Functions
  const handleBackClick = () => {
    const queryParams = new URLSearchParams(location.search);
    navigate(`/teams?${queryParams.toString()}`);
  };

  const hideAddMembersModal = () => {
    setIsShowingAddMembersModal(false);
  };

  const handleRefetch = () => {
    refetch({ uuid: teamUuid });
  };

  if (loading || loadingUserData) {
    return <Loading shouldShow={loading || loadingUserData} />;
  }

  // Only access this team if you belong to the team
  // Or if you are an admin of the team's organization
  // And your userTeams array has been loaded
  if (
    !isUserOnTeam &&
    !isCurrentUserAnAdmin &&
    !isTeamInOrganization &&
    Array.isArray(userTeams)
  ) {
    return (
      <div className="flex flex-col h-[600px] items-center justify-center">
        <DSCardWrapper
          variant="warning"
          padding="p-10"
          dataTestId="permission-denied-card"
        >
          <div className="flex flex-col items-center space-y-10">
            <h2>Whoops, we can&apos;t take you there.</h2>
            <h5 className="max-w-2xl text-center">
              You don't have permission to view this team.
            </h5>
            <DSLinkButton
              onClick={() => navigate("/teams")}
              dataTestId="return-to-teams-link"
            >
              Click here to return to your teams.
            </DSLinkButton>
          </div>
        </DSCardWrapper>
      </div>
    );
  }

  return (
    <>
      <TeamModal
        isOpen={isShowingAddMembersModal}
        onClose={hideAddMembersModal}
        team={team}
        orgAllowsDirectMemberAdd={team?.orgAllowsDirectMemberAdd}
        orgIsManualBilling={team?.orgIsManualBilling}
        hasBillingSetup={team?.hasBillingSetup}
        refetch={handleRefetch}
        orgUuid={orgUuid}
      />
      <DeleteTeamModal
        shouldShow={isShowingDeleteModal}
        hideModal={() => setIsShowingDeleteModal(false)}
        team={team}
        refetchTeams={refetch}
      />

      <PageTitle
        title={isCurrentUserAnAdmin ? "Manage Team" : "My Team"}
      />
      {team && (
        <>
          <TeamHeaderInfo team={team} />
          <div className="flex flex-row items-center justify-end gap-sm flex-wrap">
            {(userTeams?.length > 1 || isOrgAdmin) && (
              <DSButton
                variant="secondary"
                icon={faArrowLeft}
                onClick={handleBackClick}
                dataTestId="view-other-teams-button"
              >
                View Other Teams
              </DSButton>
            )}

            <OrgBillingInfo hasBillingSetup={team?.hasBillingSetup} />
            {
              // Only show the add members button if the user is an org admin
              isOrgAdmin && team?.orgAllowsDirectMemberAdd && (
                <DSButton
                  onClick={() => setIsShowingAddMembersModal(true)}
                  variant="primary"
                  icon={faUserPlus}
                  dataTestId="add-team-members-button"
                >
                  Add Team Members
                </DSButton>
              )
            }
            {!team?.orgAllowsDirectMemberAdd &&
              (isOrgAdmin || isCurrentUserAnAdmin) && (
                <>
                  <DSButton
                    onClick={() => setIsShowingAddMembersModal(true)}
                    variant="primary"
                    icon={faUserPlus}
                    dataTestId="invite-team-members-button"
                  >
                    Invite Team Members
                  </DSButton>
                  <DSButton
                    onClick={() => setIsShowingDeleteModal(true)}
                    variant="secondary"
                    icon={faTrash}
                    dataTestId="delete-team-button"
                  >
                    Delete Team
                  </DSButton>
                </>
              )}
          </div>
          <div className="pb-md">
            <HeadingWrapper heading={<h3>Current Team Members</h3>} />
            {team?.users?.length === 0 && isCurrentUserAnAdmin ? (
              <div
                className="flex flex-col items-center bg-secondary border-2 border-dashed border-primary rounded-standard py-10 px-sm w-full my-md hover:bg-brand-subtle transition-all cursor-pointer"
                onClick={() => setIsShowingAddMembersModal(true)}
              >
                <img
                  className="flex self-center"
                  width="100px"
                  src={PartyScribbles}
                  alt="Party Scribbles"
                />
                <h4 className="mt-md mb-sm">
                  No one&apos;s here yet...
                </h4>
                <h5 className="text-secondary max-w-3xl">
                  {team?.orgAllowsDirectMemberAdd ? "Add" : "Invite"}{" "}
                  team members to get started!
                </h5>
              </div>
            ) : team?.users?.length === 0 && !isCurrentUserAnAdmin ? (
              <div className="flex flex-col items-center bg-secondary border-2 border-dashed border-primary rounded-standard py-10 px-sm w-full my-md hover:bg-brand-subtle transition-all">
                <img
                  className="flex self-center"
                  width="100px"
                  src={PartyScribbles}
                  alt="Party Scribbles"
                />
                <h4 className="mt-md mb-sm">
                  No one&apos;s here yet...
                </h4>
                <h5 className="text-secondary max-w-3xl">
                  Contact a team admin to{" "}
                  {team?.orgAllowsDirectMemberAdd ? "add" : "invite"}{" "}
                  team members!
                </h5>
              </div>
            ) : (
              <EditExistingTeamMembers
                team={team}
                isCurrentUserAnAdmin={isCurrentUserAnAdmin}
                refetchTeam={refetch}
              />
            )}
          </div>
          {isCurrentUserAnAdmin && team?.teamInvites?.length > 0 ? (
            <div className="pb-md">
              <HeadingWrapper heading={<h3>Invited to Team</h3>} />
              <EditInvitedTeamMembers
                teamUuid={teamUuid}
                team={team}
                refetch={refetch}
              />
            </div>
          ) : null}
          <div className="pb-md">
            <HeadingWrapper
              heading={<h3>Team Default Templates</h3>}
            />
            <DefaultTemplatesTable />
          </div>
        </>
      )}
    </>
  );
}

function TeamHeaderInfo({ team }) {
  return (
    <div className="my-md md:my-10">
      <DSCardWrapper
        variant="info"
        padding="p-md"
        dataTestId="team-header-info"
      >
        <div className="flex flex-col md:flex-row md:items-center space-x-sm md:space-x-md">
          <div className="h-10 w-10 flex items-center justify-center rounded-full bg-brand text-white ml-1 md:ml-md shrink-0 grow-0 aspect-square">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="flex flex-col w-full">
            <h3 className="truncate w-[90%]" title={team?.name}>
              {team?.name}
            </h3>
            <h5 className="text-xs text-secondary">
              Team ID: {team?.uuid}
            </h5>
          </div>
        </div>
      </DSCardWrapper>
    </div>
  );
}
